import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import backendApi from "../api/backendApi";
import { FaDownload } from "react-icons/fa";
import { protectedTheme } from "./ui/protectedTheme";

const ChandaReceipt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState(null);
  const DUITNOWQR = "DuitNow QR";
  const USER_TRANSACTIONS_RETURN_KEY = "userTransactionsReturnTo";
  const requestedReturnTo =
    location.state?.returnTo ||
    sessionStorage.getItem(USER_TRANSACTIONS_RETURN_KEY) ||
    "/transactions";
  const returnTo = requestedReturnTo.startsWith("/transactions")
    ? requestedReturnTo
    : "/transactions";
  const handleReturn = () => navigate(returnTo);

  useEffect(() => {
    if (location.state?.returnTo?.startsWith("/transactions")) {
      sessionStorage.setItem(USER_TRANSACTIONS_RETURN_KEY, location.state.returnTo);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const res = await backendApi.get(`/chanda/receipt/${id}`);
        setReceipt(res.data);
        setError(null);
      } catch (err) {
        if (err.response?.status === 404) {
          setError("Receipt not found. Please check your transaction ID.");
        } else {
          setError("An error occurred while fetching the receipt.");
        }
      }
    };
    fetchReceipt();
  }, [id]);

  // helpers
  const formatToken = (t) =>
    t === "TAX"
      ? ""
      : t === "TRX_FEE"
      ? "Transaction Fee"
      : t
          .toLowerCase()
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");

  const formatCategoryName = (name = "") =>
    name.split(/\s+/).filter(Boolean).map(formatToken).join(" ");

  const byCategory = (a, b) => {
    const aIsTrx = /\bTRX_FEE\b/.test(a.category?.name || "");
    const bIsTrx = /\bTRX_FEE\b/.test(b.category?.name || "");
    if (aIsTrx && !bIsTrx) return 1;
    if (!aIsTrx && bIsTrx) return -1;
    return formatCategoryName(a.category?.name).localeCompare(
      formatCategoryName(b.category?.name),
      "en",
      { sensitivity: "base" }
    );
  };

  const isUserVisibleCategory = (pc) =>
    String(pc?.category?.name || "").toUpperCase() !== "TAX";

  // error states
  if (error) {
    return (
      <div className={`${protectedTheme.page} flex flex-col items-center justify-center px-4 py-10 text-center`}>
        <h2 className="mb-2 text-2xl font-black text-zinc-950">
          Receipt Not Found
        </h2>
        <p className="mb-6 text-zinc-500">{error}</p>
        <button
          onClick={handleReturn}
          className={protectedTheme.primaryButton}
        >
          Return
        </button>
      </div>
    );
  }

  if (!receipt) return <div className="text-center mt-10">Loading...</div>;

  const {
    status,
    paymentMonth,
    paymentCategories = [],
    paymentDate,
    method,
    paymentReference,
    intentUrl,
    amount,
    payer = {},
  } = receipt;

  const statusU = String(status || "").toUpperCase();
  const visiblePaymentCategories = paymentCategories.filter(isUserVisibleCategory);
  const totalAmount = Number(amount ?? visiblePaymentCategories.reduce(
    (sum, pc) => sum + parseFloat(pc.amount || 0),
    0
  ));

  // INITIATED (Pending) view — no requery button
  if (statusU === "INITIATED") {
    return (
      <div className={`${protectedTheme.page} flex items-center justify-center px-4 py-4`}>
        <div className="relative w-full max-w-md rounded-3xl bg-white p-6 text-center shadow">
          <img
            src="/Khuddam-Logo-Word.svg"
            alt="Khuddam Logo"
            className="h-16 mx-auto"
          />
          <h2 className="mt-4 mb-2 text-xl font-black text-zinc-950">Payment Pending</h2>
          <p className="text-sm text-zinc-600">
            Your payment has been initiated and is awaiting confirmation from
            the bank/provider.
          </p>
          <p className="mt-2 text-xs text-zinc-500">
            We’ll update the status on our side. If it doesn’t complete, it will
            eventually expire and you can try again.
          </p>
          {method === DUITNOWQR && (
            <button
              onClick={() => (window.location.href = intentUrl)}
              className={`mt-4 w-full ${protectedTheme.primaryButton}`}
            >
              Pay Now
            </button>
          )}
          <button
            onClick={handleReturn}
            className={`mt-4 w-full ${
              method === DUITNOWQR
                ? protectedTheme.secondaryButton
                : protectedTheme.primaryButton
            }`}
          >
            Return
          </button>
        </div>
      </div>
    );
  }

  // CANCELLED or EXPIRED view
  if (statusU === "CANCELLED" || statusU === "EXPIRED") {
    const msg =
      statusU === "CANCELLED"
        ? "The payment was cancelled."
        : "The payment attempt has expired.";
    return (
      <div className={`${protectedTheme.page} flex items-center justify-center px-4 py-4`}>
        <div className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow">
          <div className="flex justify-center">
            <img
              src="/Khuddam-Logo-Word.svg"
              alt="Khuddam Logo"
              className="h-16"
            />
          </div>
          <h2 className="mt-2 mb-2 text-center text-xl font-black text-zinc-950">
            Payment Not Completed
          </h2>
          <p className="mb-4 text-center text-sm text-zinc-600">{msg}</p>

          <div className="space-y-3 border-t border-zinc-200 pt-4 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-500">Transaction ID</span>
              <span className="font-medium break-all">
                {paymentReference || "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Payment Method</span>
              <span className="font-medium text-right">{method || "-"}</span>
            </div>
          </div>

          <button
            onClick={() => navigate("/pay")}
            className={`mt-4 w-full ${protectedTheme.primaryButton}`}
          >
            Make Another Payment
          </button>
          <button
            onClick={handleReturn}
            className={`mt-2 w-full ${protectedTheme.secondaryButton}`}
          >
            Return
          </button>
        </div>
      </div>
    );
  }

  // FAILED view
  if (statusU === "FAILED") {
    return (
      <div className={`${protectedTheme.page} flex items-center justify-center px-4 py-4`}>
        <div className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow">
          <div className="flex justify-center">
            <img
              src="/Khuddam-Logo-Word.svg"
              alt="Khuddam Logo"
              className="h-16"
            />
          </div>

          <h2 className="text-center text-xl font-bold mt-2 mb-2 text-red-600">
            Payment Failed
          </h2>
          <p className="mb-4 text-center text-sm text-zinc-600">
            The transaction failed.
          </p>

          <div className="space-y-3 border-t border-zinc-200 pt-4 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-500">Transaction ID</span>
              <span className="font-medium break-all">
                {paymentReference || "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Attempted on</span>
              <span className="font-medium">
                {paymentDate
                  ? new Date(paymentDate).toLocaleString("en-MY", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })
                  : "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Payment Method</span>
              <span className="font-medium text-right">{method || "-"}</span>
            </div>
          </div>

          <button
            onClick={() => navigate("/pay")}
            className={`mt-4 w-full ${protectedTheme.primaryButton}`}
          >
            Make Another Payment
          </button>
          <button
            onClick={handleReturn}
            className={`mt-2 w-full ${protectedTheme.secondaryButton}`}
          >
            Return
          </button>
        </div>
      </div>
    );
  }

  // SUCCESS view (default receipt)
  return (
    <div className={`${protectedTheme.page} flex items-center justify-center px-4 py-4`}>
      <div className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow">
        {/* Logo */}
        <div className="flex justify-center">
          <img
            src="/Khuddam-Logo-Word.svg"
            alt="Khuddam Logo"
            className="h-16"
          />
        </div>

        <h2 className="mt-2 mb-4 text-center text-xl font-black text-zinc-950">
          Chanda Receipt
        </h2>

        <div className="space-y-3 border-t border-zinc-200 pt-4 text-sm">
          <div className="flex justify-between">
            <span className="text-zinc-500">Name</span>
            <span className="font-medium break-all">
              {formatCategoryName(payer.firstName + "_" + payer.lastName)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-500">AIMS No</span>
            <span className="font-medium">{payer.aimsNo}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-500">Muqami</span>
            <span className="font-medium">
              {formatCategoryName(payer.muqami)}
            </span>
          </div>
        </div>

        <div className="mt-4 space-y-3 border-t border-zinc-200 pt-4 text-sm">
          <div className="flex justify-between">
            <span className="text-zinc-500">Transaction ID</span>
            <span className="font-medium break-all">{paymentReference}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-500">Payment for month</span>
            <span className="font-medium">
              {new Date(`${paymentMonth}-01`).toLocaleString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-500">Paid on</span>
            <span className="font-medium">
              {paymentDate
                ? new Date(paymentDate).toLocaleString("en-MY", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })
                : "N/A"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-500">Payment Method</span>
            <span className="font-medium text-right">{method}</span>
          </div>
          <div className="space-y-3 border-t pt-2">
            {[...visiblePaymentCategories].sort(byCategory).map((pc) => (
              <div className="flex justify-between" key={pc.id}>
                <span className="text-zinc-500">
                  {formatCategoryName(pc.category?.name)}
                </span>
                <span className="font-bold text-zinc-800">
                  RM {Number(pc.amount).toFixed(2)}
                </span>
              </div>
            ))}
          </div>

          <div className="flex justify-between border-t pt-2">
            <span className="text-zinc-500">Total</span>
            <span className="font-bold text-zinc-800">
              RM {totalAmount.toFixed(2)}
            </span>
          </div>
        </div>

        {/* PDF Receipt Button (only when success) */}
        <button
          onClick={async () => {
            try {
              const res = await backendApi.get(`/chanda/receipt/${id}/pdf`, {
                responseType: "blob",
              });
              const blob = new Blob([res.data], { type: "application/pdf" });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `receipt-${id}.pdf`;
              a.click();
              window.URL.revokeObjectURL(url);
            } catch (err) {
              console.error("Error downloading PDF:", err);
            }
          }}
          className={`mt-4 flex w-full items-center justify-center gap-2 ${protectedTheme.secondaryButton}`}
        >
          <FaDownload />
          Get PDF Receipt
        </button>

        {/* Back to transactions */}
        <button
          onClick={handleReturn}
          className={`mt-4 w-full ${protectedTheme.primaryButton}`}
        >
          Return
        </button>
      </div>
    </div>
  );
};

export default ChandaReceipt;
